import moment from 'moment-timezone'
import { calculateAgentTopupProfit, formatCurrency, formatDate, formatTime } from '@smarttransit/common'
import { addAlert } from '../../utilities/helpers'
import { getAgentTopupProfitStats } from '../../services/agents-service'

function defaultDateFrom () {
  return moment().subtract(30, 'days').format('Y-MM-DD')
}

function defaultDateTo () {
  return moment().format('Y-MM-DD')
}

function centsToGhsInput (cents) {
  return (Number(cents || 0) / 100).toFixed(2)
}

function parseGhsToCents (value) {
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return 0
  }
  return Math.round(parsed * 100)
}

function formatCount (value) {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) {
    return '0'
  }
  return parsed.toLocaleString('en-GB', { maximumFractionDigits: 1 })
}

export default {
  name: 'agent-topup-profit-dialog',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    initialConversionRatePercent: {
      type: [Number, String],
      default: null
    },
    currencySymbol: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      dateFrom: defaultDateFrom(),
      dateTo: defaultDateTo(),
      conversionRatePercent: '120',
      targetProfitGhs: '500',
      workingDays: '20',
      topupAmountGhs: '',
      stats: null,
      statsLoading: false,
      statsError: null,
      statsLoadId: 0,
      periodAveragesExpanded: false
    }
  },
  computed: {
    dialogModel: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    },
    currencyPrefix () {
      return this.currencySymbol
    },
    targetCents () {
      return parseGhsToCents(this.targetProfitGhs)
    },
    calc () {
      return calculateAgentTopupProfit({
        topupAmountAsCents: parseGhsToCents(this.topupAmountGhs),
        conversionRatePercent: Number(this.conversionRatePercent),
        targetProfitAsCents: parseGhsToCents(this.targetProfitGhs),
        workingDays: Number(this.workingDays)
      })
    },
    rateHasNoProfit () {
      const rate = Number(this.conversionRatePercent)
      return Number.isFinite(rate) && rate > 0 && rate <= 100
    },
    todayDate () {
      return defaultDateTo()
    }
  },
  created () {
    if (this.value) {
      this.resetAndLoad()
    }
  },
  watch: {
    value (open) {
      if (open) {
        this.resetAndLoad()
      }
    }
  },
  methods: {
    closeDialog () {
      this.dialogModel = false
    },
    formatMoney (cents) {
      return formatCurrency(cents, this.currencyPrefix)
    },
    formatCount (value) {
      return formatCount(value)
    },
    formatDateLabel (value) {
      const date = this.toDisplayDate(value)
      return date ? formatDate(date) : '—'
    },
    toDisplayDate (value) {
      if (!value) {
        return null
      }

      const asString = String(value)

      if (/^\d{4}-\d{2}-\d{2}$/.test(asString)) {
        return moment(asString, 'YYYY-MM-DD')
      }

      return value
    },
    resolveInitialRate () {
      const fromProp = Number(this.initialConversionRatePercent)
      if (Number.isFinite(fromProp) && fromProp > 0) {
        return String(fromProp)
      }
      return '120'
    },
    resetAndLoad () {
      this.dateFrom = defaultDateFrom()
      this.dateTo = defaultDateTo()
      this.conversionRatePercent = this.resolveInitialRate()
      this.targetProfitGhs = '500'
      this.workingDays = '20'
      this.statsError = null
      this.periodAveragesExpanded = false
      this.loadStats()
    },
    async loadStats () {
      if (!this.dateFrom || !this.dateTo) {
        return
      }

      const requestId = this.statsLoadId + 1
      this.statsLoadId = requestId
      this.statsLoading = true
      this.statsError = null
      try {
        const stats = await getAgentTopupProfitStats({
          periodStart: this.dateFrom,
          periodEnd: this.dateTo
        })
        if (requestId !== this.statsLoadId) {
          return
        }
        this.stats = stats
        if (stats && stats.meanAmountCents) {
          this.topupAmountGhs = centsToGhsInput(stats.meanAmountCents)
        }
      } catch (err) {
        if (requestId !== this.statsLoadId) {
          return
        }
        this.stats = null
        this.statsError = (err && err.message) || 'Could not load top-up averages'
        addAlert({ message: err, type: 'error' })
      } finally {
        if (requestId === this.statsLoadId) {
          this.statsLoading = false
        }
      }
    }
  }
}
